import Vue from 'vue'
import Router from 'vue-router'
import Login from '@/views/auth/Login.view'
import Signup from '@/views/auth/Signup.view'
import Administration from '@/views/administration/Administration.view'
import Logs from '@/views/administration/log/Logs.view'
import LogListView from '@/views/administration/log/LogList.view'
import Integrations from '@/views/administration/integrations/Integrations.view'
import NewIntegration from '@/views/administration/integrations/NewIntegration.view'
import SmartsuppAuthFlow from '@/components/administration/integration/wizard/smartsupp/SmartsuppAuthFlow'
import IntegrationDetail from '@/views/administration/integrations/IntegrationDetail.view'
import Channels from '@/views/administration/channel/Channels.view'
import NewChannel from '@/views/administration/channel/NewChannel.view'
import ChannelList from '@/views/administration/channel/ChannelList.view'
import ChannelDetail from '@/views/administration/channel/channel-detail/ChannelDetail.view'
import IntegrationList from '@/views/administration/integrations/IntegrationList.view'
import ChannelIntegrations from '@/views/administration/channel/channel-detail/components/ChannelIntegrations.view'
import ChannelSettings from '@/views/administration/channel/channel-detail/components/ChannelSettings.view'
import ChannelInfo from '@/views/administration/channel/channel-detail/components/ChannelInfo.view'
import Webhook from '@/views/administration/channel/channel-detail/components/Webhook.view'
import Contacts from '@/views/administration/channel/channel-detail/components/Contacts.view'
import ChannelLogs from '@/views/administration/channel/channel-detail/components/ChannelLogs.view'
import Settings from '@/views/administration/settings/Settings.view'
import Api from '@/views/administration/settings/Api.view'
import Users from '@/views/administration/user/Users.view'
import UserList from '@/views/administration/user/UserList.view'
import EditUser from '@/views/administration/user/UserEdit.view'
import NotFound from '@/views/NotFound.view'
import authService from '@/services/security/hosted-auth.service'
import pathHolder from '@/services/security/path-after-login.holder'
import store from '@/config/store'

Vue.use(Router)

function getChannelTitle() {
  return Promise.resolve(store.administration.channelDetail.channel.name)
}

function getIntegrationTitle() {
  return Promise.resolve(store.administration.integrationDetail.integration.name)
}

function getUserName() {
  return Promise.resolve(store.administration.userEdit.user.name)
}

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  linkActiveClass: 'active',
  routes: [
    {
      path: '/',
      redirect: {name: 'administration.channels.list'}
    },
    {
      path: '/login',
      name: 'login',
      component: Login
    },
    {
      path: '/logout',
      beforeEnter: (to, from, next) => {
        authService.logout()
        next()
      }
    },
    {
      path: '/signup',
      name: 'signup',
      component: Signup
    },
    {
      path: '/administration',
      component: Administration,
      redirect: {name: 'administration.channels.list'},
      children: [
        {
          path: 'channels',
          component: Channels,
          name: 'administration.channels',
          redirect: {name: 'administration.channels.list'},
          meta: {
            breadcrumbs: [{
              title: 'Channels'
            }]
          },
          children: [
            {
              path: 'new',
              name: 'administration.channels.new',
              component: NewChannel,
              meta: {
                breadcrumbs: [{
                  title: 'New Channel'
                }]
              }
            },
            {
              path: '',
              name: 'administration.channels.list',
              component: ChannelList
            }
          ]
        },

        {
          path: 'channels/:id/integrations/new',
          name: 'administration.channel-detail.integrations.new',
          component: NewIntegration,
          meta: {
            breadcrumbs: [
              {
                title: getChannelTitle,
                pathToFunction: (route) => {
                  const channelId = route.params.id

                  if (channelId) {
                    return {
                      name: 'administration.channel-detail.integrations',
                      params: {id: channelId}
                    }
                  }

                  return {
                    name: 'administration.integrations.list'
                  }
                }
              },
              {
                title: 'New Integration'
              }
            ]
          }
        },

        {
          path: 'channels/:id/integrations/smartsupp-auth-flow',
          name: 'administration.channel-detail.integrations.smartsupp-auth-flow',
          component: SmartsuppAuthFlow,
          meta: {
            breadcrumbs: [
              {
                title: 'New Smartsupp Integration'
              }
            ]
          }
        },

        {
          path: 'channels/:id',
          component: ChannelDetail,
          meta: {
            breadcrumbs: [{
              title: 'Channels',
              pathName: 'administration.channels'
            }]
          },
          children: [
            {
              path: 'info',
              name: 'administration.channel-detail.info',
              component: ChannelInfo,
              meta: {
                breadcrumbs: [{
                  title: getChannelTitle
                }]
              }
            },
            {
              path: 'webhook',
              name: 'administration.channel-detail.webhook',
              component: Webhook,
              meta: {
                breadcrumbs: [{
                  title: getChannelTitle
                }]
              }
            },
            {
              path: 'contacts',
              name: 'administration.channel-detail.contacts',
              component: Contacts,
              meta: {
                breadcrumbs: [{
                  title: getChannelTitle
                }]
              }
            },
            {
              path: 'logs',
              name: 'administration.channel-detail.logs',
              component: ChannelLogs,
              meta: {
                breadcrumbs: [{
                  title: getChannelTitle
                }]
              }
            },
            {
              path: 'integrations',
              name: 'administration.channel-detail.integrations',
              component: ChannelIntegrations,
              meta: {
                breadcrumbs: [{
                  title: getChannelTitle
                }]
              }
            },
            {
              path: 'settings',
              name: 'administration.channel-detail.settings',
              component: ChannelSettings,
              meta: {
                breadcrumbs: [{
                  title: getChannelTitle
                }]
              }
            }
          ]
        },

        {
          path: 'logs',
          component: Logs,
          name: 'administration.logs',
          redirect: {name: 'administration.logs.list'},
          meta: {
            breadcrumbs: [{
              title: 'Logs',
              pathToFunction: (route) => {
                const channelId = route.query.channelId

                if (channelId) {
                  return {
                    name: 'administration.channel-detail.logs',
                    params: {id: channelId}
                  }
                }

                return {
                  name: 'administration.logs.list'
                }
              }
            }]
          },
          children: [
            {
              path: '',
              name: 'administration.logs.list',
              component: LogListView
            }
          ]
        },

        {
          path: 'integrations',
          component: Integrations,
          name: 'administration.integrations',
          redirect: {name: 'administration.integrations.list'},
          meta: {
            breadcrumbs: [{
              title: 'Integrations',
              pathToFunction: (route) => {
                const channelId = route.query.channelId

                if (channelId) {
                  return {
                    name: 'administration.channel-detail.integrations',
                    params: {id: channelId}
                  }
                }

                return {
                  name: 'administration.integrations.list'
                }
              }
            }]
          },
          children: [
            {
              path: '',
              name: 'administration.integrations.list',
              component: IntegrationList
            },
            {
              path: 'new',
              name: 'administration.integrations.new',
              component: NewIntegration,
              meta: {
                breadcrumbs: [{
                  title: 'New Integration'
                }]
              }
            }
          ]
        },
        {
          path: 'integrations/smartsupp-auth-flow',
          name: 'administration.integrations.smartsupp-auth-flow',
          component: SmartsuppAuthFlow,
          meta: {
            breadcrumbs: [
              {
                title: 'New Smartsupp Integration'
              }
            ]
          }
        },
        {
          path: 'integrations/:id',
          name: 'administration.integrations.detail',
          component: IntegrationDetail,
          meta: {
            breadcrumbs: [
              {
                title: 'Integrations',
                pathName: 'administration.integrations'
              },
              {
                title: getIntegrationTitle
              }
            ]
          }
        },
        {
          path: 'settings',
          component: Settings,
          redirect: {name: 'administration.settings.api'},
          meta: {
            breadcrumbs: [{
              title: 'Settings'
            }]
          },
          children: [
            {
              path: 'api',
              name: 'administration.settings.api',
              component: Api,
              meta: {
                breadcrumbs: [{
                  title: 'Api'
                }]
              }
            },
            {
              path: 'users',
              name: 'administration.settings.users',
              redirect: {name: 'administration.settings.users.list'},
              component: Users,
              meta: {
                breadcrumbs: [{
                  title: 'Users'
                }]
              },
              children: [
                {
                  path: '',
                  name: 'administration.settings.users.list',
                  component: UserList
                },
                {
                  path: ':id/edit',
                  name: 'administration.settings.users.edit',
                  component: EditUser,
                  meta: {
                    breadcrumbs: [{
                      title: getUserName
                    }]
                  }
                }
              ]
            }
          ]
        }
      ]
    },
    {
      path: '/not-found',
      name: 'not-found',
      component: NotFound
    },
    {
      path: '*',
      component: NotFound
    }
  ]
})

router.beforeEach((to, from, next) => {
  if (to.path === '/callback' && !authService.isAuthenticated()) {
    return authService.handleAuthentication()
  }

  if (to.path === '/login' && authService.isAuthenticated()) {
    return next('/administration/channels')
  }

  if (authService.isAuthenticated() || to.path === '/login' || to.path === '/signup') return next()

  log.debug('router - beforeEach() - user not authenticated, showing the login')
  pathHolder.storePath(to.fullPath)
  next('/login')
})

export default router
